import {
  Children,
  isValidElement,
  type InputHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  useId,
} from 'react';
import InputField from './shared/components/ui/fields/InputField';
import Select, { type SelectOption } from './shared/components/ui/fields/Select';
import Textarea from './shared/components/ui/fields/Textarea';
import LabelText from './shared/components/ui/LabelText';

type Common = { label: string; error?: string; hint?: string };

function FieldShell({
  label,
  htmlFor,
  required,
  children,
}: {
  label: string;
  htmlFor: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <LabelText htmlFor={htmlFor} required={required}>
        {label}
      </LabelText>
      {children}
    </div>
  );
}

/** Kolom isian WDS dengan label di atasnya. `className` dipasang pada elemen <input>. */
export function TextField({
  label,
  error,
  hint,
  className,
  id,
  ...props
}: Common & Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <FieldShell label={label} htmlFor={fid} required={props.required}>
      <InputField id={fid} error={error} hint={hint} inputClassName={className} {...props} />
    </FieldShell>
  );
}

export function TextAreaField({
  label,
  error,
  hint,
  className,
  id,
  ...props
}: Common & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <FieldShell label={label} htmlFor={fid} required={props.required}>
      <Textarea id={fid} error={error} hint={hint} className={className} {...props} />
    </FieldShell>
  );
}

/** Ubah anak `<option>` menjadi opsi Select WDS (nilai kosong = placeholder). */
function optionsOf(children: ReactNode): { options: SelectOption[]; placeholder?: string } {
  const options: SelectOption[] = [];
  let placeholder: string | undefined;
  const walk = (nodes: ReactNode) =>
    Children.forEach(nodes, (c) => {
      if (!isValidElement(c)) return;
      const el = c as ReactElement<{ value?: string | number; children?: ReactNode }>;
      if (el.type === 'option') {
        const label = Children.toArray(el.props.children).join('');
        const value = el.props.value === undefined ? label : String(el.props.value);
        if (value === '') placeholder = label;
        else options.push({ label, value });
      } else walk(el.props.children);
    });
  walk(children);
  return { options, placeholder };
}

/**
 * Pilihan WDS dengan API <select> lama: anak <option>, `value`, dan `onChange` bergaya event
 * (`e.target.value`). Cukup untuk sebagian besar form; opsi dengan pencarian pakai `Select` langsung.
 */
export function SelectField({
  label,
  error,
  hint,
  className,
  children,
  value,
  onChange,
  disabled,
  required,
  id,
}: Common &
  Pick<
    SelectHTMLAttributes<HTMLSelectElement>,
    'className' | 'disabled' | 'id' | 'children' | 'required'
  > & {
    value?: string | number | readonly string[];
    onChange?: (e: { target: { value: string }; currentTarget: { value: string } }) => void;
  }) {
  const auto = useId();
  const fid = id ?? auto;
  const { options, placeholder } = optionsOf(children);
  const v = value == null ? '' : String(value);
  return (
    <FieldShell label={label} htmlFor={fid} required={required}>
      <Select
        options={options}
        value={v}
        placeholder={placeholder ?? 'Pilih…'}
        clearable={false}
        searchable={options.length > 8}
        disabled={disabled}
        error={error}
        hint={hint}
        className={className}
        onChange={(next) => onChange?.({ target: { value: next }, currentTarget: { value: next } })}
      />
    </FieldShell>
  );
}
